// Datepicker component for anyguide completely agnostic of the FormStore.
// For mobile devices a native <input type="date" /> is rendered. On web displays, we use
// the pikaday lib.
// Usage:
//   let props = {
//     onChange: function(date) { ... } // Required. The component will pass the new date as a string in the format specified in the props.
//     dateFormat: 'MMM YY', // the format for displaying the date in the input
//     defaultDateFormat: 'YYYY-MM-DD', // optional, defaults to 'YYYY-MM-DD', used for conversions
//     value: '2015-01-01', // the date value in string format
//     startDate: '2015-01-01', // optional
//     disableDayFn: function(date) { ... } // when returning a truthy value, the day will be disabled
//     onDraw: function() { ... } // callback for pikaday re-drawing when changing month. Allows interacting with the calendar.
//     placeholder: '', // A placeholder to display when there is no date selected
//   }
//   <Datepicker {...props} />
//
import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import * as Pikaday from 'pikaday';
import moment from 'moment';
import AppStore from 'stores/anyguide/app';
import $ from 'jquery';
import { t, findLocale } from 'services/translation';

import 'pikaday/css/pikaday.css';
import './index.scss';

const isDesktop = function (props = null) {
  if (props && props.forceMobile) return false;
  return !AppStore.getState().isMobile || AppStore.getState().isTablet;
};

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
const NATIVE_INPUT_FORMAT = 'YYYY-MM-DD';

export default class Datepicker extends React.Component {
  static displayName = 'Datepicker';
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    dateFormat: PropTypes.string,
    defaultDateFormat: PropTypes.string,
    startDate: PropTypes.string,
    value: PropTypes.string,
    disableDayFn: PropTypes.func,
    onDraw: PropTypes.func,
    placeholder: PropTypes.string,
    displayToday: PropTypes.bool,
    displayDesktopOnMobile: PropTypes.bool,
  };
  static defaultProps = {
    dateFormat: DEFAULT_DATE_FORMAT,
    defaultDateFormat: DEFAULT_DATE_FORMAT,
    startDate: null,
    value: null,
    disableDayFn: null,
    onDraw() {},
    placeholder: '',
    displayToday: false,
    displayDesktopOnMobile: false,
  };

  componentDidMount() {
    if (isDesktop(this.props)) {
      this._setupDatepicker();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.startDate != nextProps.startDate) {
      this._setupDatepicker(nextProps.startDate);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.pikaday) {
      const calendar = this.pikaday.calendars[0];
      this.pikaday.gotoDate(new Date(calendar.year, calendar.month));

      if (prevProps.value != this.props.value) {
        this._setupDatepicker();
      }
    }
  }

  componentWillUnmount() {
    if (isDesktop(this.props)) {
      this._teardownDatePicker();
    }
  }

  // Returns day of week to start with
  // 0 => Sunday
  // 1 => Monday
  weekStartDay() {
    if (this.props.weekStartDay === 0) return 0;
    if (this.props.weekStartDay > 0) return this.props.weekStartDay;
    return 1;
  }

  _i18nSettings() {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const weekDays = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const weekDaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return {
      previousMonth: t('PREVIOUS_MONTH'),
      nextMonth: t('NEXT_MONTH'),
      months: months.map((m) => t(m)),
      weekdays: weekDays.map((wd) => t(wd)),
      weekdaysShort: weekDaysShort.map((wds) => t(wds)),
    };
  }

  _setupDatepicker(startDate = null) {
    const onDrawFunc = this.props.onDraw;
    this._teardownDatePicker();
    const minDate = startDate || this._minDate();
    this.pikaday = new Pikaday({
      field: findDOMNode(this.refs.input),
      firstDay: this.weekStartDay(),
      theme: 'anyguide',
      format: this.props.dateFormat,
      i18n: this._i18nSettings(),
      minDate,
      defaultDate: this._date(),
      disableDayFn: this.props.disableDayFn,
      onDraw() {
        onDrawFunc(this.calendars[0]);
      },
      onSelect: (date) => {
        this.props.onChange(moment(date).format(this.props.defaultDateFormat));
      },
    });
  }

  _teardownDatePicker() {
    if (this.pikaday) {
      this.pikaday.destroy();
    }
  }

  _date() {
    return this.props.value
      ? moment(this.props.value, this.props.defaultDateFormat).toDate()
      : '';
  }

  _minDate() {
    if (this.props.startDate) {
      return moment(
        this.props.startDate,
        this.props.defaultDateFormat
      ).toDate();
    }
  }

  _dateForInput() {
    const date = this._date();
    if (this.props.displayToday && isDesktop(this.props)) {
      const today = moment().format('L');
      const pickerDate = moment(date).format('L');
      if (today === pickerDate) return 'Today';
    }
    const format = isDesktop(this.props)
      ? this.props.dateFormat
      : NATIVE_INPUT_FORMAT;
    return date === ''
      ? date
      : moment(date).locale(findLocale()).format(format);
  }

  _changed(e) {
    this.props.onChange(
      moment(e.currentTarget.value, NATIVE_INPUT_FORMAT).format(
        this.props.defaultDateFormat
      )
    );
  }

  _focusDateInput() {
    $(findDOMNode(this.refs.input)).focus();
  }

  render() {
    let type = 'date';
    let icon = null;
    let readonly = null;

    if (isDesktop(this.props)) {
      type = 'text';
      icon = (
        <i
          className="af-calendar-ar"
          onClick={this._focusDateInput.bind(this)}
        />
      );
      readonly = 'readonly';
    } else if (this.props.displayDesktopOnMobile) {
      type = 'date';
      icon = (
        <i
          className="af-calendar-ar"
          onClick={this._focusDateInput.bind(this)}
        />
      );
      readonly = 'readonly';
    }

    return (
      <div
        className={`Datepicker ${this.props.disabled ? 'disabled' : ''}`}
        aria-labelledby={this.props.ariaLabelledBy}
      >
        <input
          ref="input"
          type={type}
          value={this._dateForInput()}
          readOnly={readonly}
          disabled={this.props.disabled}
          onChange={this._changed.bind(this)}
          onClick={
            this.props.openOnClickInput ? this._focusDateInput.bind(this) : null
          }
          placeholder={this.props.placeholder}
          min={this.props.min}
          max={this.props.max}
          data-testid="datepicker"
          aria-label={this.props.ariaLabel}
        />
        {icon}
      </div>
    );
  }
}
